import { PersonSelect } from '../ui/PersonSelect';
import { useMemo, useState } from 'react';
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addWeeks,
  addMonths,
  format,
} from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from '../ui/Button';
import { ListPageLayout } from '../ui/ListPageLayout';
import { ViewSwitch } from '../ui/ListToolbar';
import { FaList, FaPlus, FaTableList } from '../ui/FaIcons';
import { useTimeEntries } from '../../hooks/queries/useTimeEntries';
import { useDossiers } from '../../hooks/queries/useDossiers';
import { useCollaborators } from '../../hooks/queries/useCollaborators';
import { useAuthStore } from '../../store/useAuthStore';
import { aggregateTimesheet } from '../../services/timeTracking/timesheetAggregate';
import { timesheetToCsv } from '../../services/timeTracking/timesheetCsv';
import { TimesheetGrid } from './TimesheetGrid';
import { TimesheetLines } from './TimesheetLines';
import { TimeEntryModal } from '../timeTracking/TimeEntryModal';
import type { TimeEntry } from '../../services/timeTracking/timeTrackingService';

type Mode = 'week' | 'month';
type Presentation = 'lines' | 'grid';

export function TimesheetPage() {
  const role = useAuthStore((s) => s.collaborator?.role);
  const myId = useAuthStore((s) => s.collaborator?.id);
  const isAdmin = role === 'admin';

  const [mode, setMode] = useState<Mode>('week');
  const [presentation, setPresentation] = useState<Presentation>('lines');
  const [cursor, setCursor] = useState(new Date());
  const [collaboratorId, setCollaboratorId] = useState<string | undefined>(undefined);
  const [createDossierId, setCreateDossierId] = useState('');
  const [editing, setEditing] = useState<TimeEntry | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const { from, to } = useMemo(() => {
    if (presentation === 'lines') {
      const start = new Date(cursor.getFullYear(), 0, 1);
      const end = new Date(cursor.getFullYear(), 11, 31, 23, 59, 59);
      return { from: start.toISOString(), to: end.toISOString() };
    }
    if (mode === 'week') {
      return {
        from: startOfWeek(cursor, { weekStartsOn: 1 }).toISOString(),
        to: endOfWeek(cursor, { weekStartsOn: 1 }).toISOString(),
      };
    }
    return {
      from: startOfMonth(cursor).toISOString(),
      to: endOfMonth(cursor).toISOString(),
    };
  }, [mode, cursor, presentation]);

  const effectiveCollaboratorId = isAdmin ? (collaboratorId ?? myId) : myId;

  const { data: entries } = useTimeEntries({
    from,
    to,
    collaborator_id: effectiveCollaboratorId,
  });

  const { data: dossiers } = useDossiers({});
  const { data: collaborators } = useCollaborators();

  const dossierLabels: Record<string, string> = useMemo(() => {
    const m: Record<string, string> = {};
    for (const d of dossiers ?? []) {
      m[d.id] = d.rg_number ? `${d.rg_number} — ${d.title}` : d.title;
    }
    return m;
  }, [dossiers]);

  const dossierRefs = useMemo(() => {
    const m: Record<string, { ref: string | null; title: string }> = {};
    for (const d of dossiers ?? []) {
      const ref = [d.number, d.rg_number ? `RG ${d.rg_number}` : null].filter(Boolean).join(' · ');
      m[d.id] = { ref: ref || null, title: d.title };
    }
    return m;
  }, [dossiers]);

  const dossierIds = useMemo(
    () =>
      Array.from(
        (entries ?? []).reduce<Set<string>>((acc, e) => {
          acc.add(e.dossier_id);
          return acc;
        }, new Set()),
      ),
    [entries],
  );

  const grid = useMemo(
    () =>
      aggregateTimesheet(
        (entries ?? []).map((e) => ({
          dossier_id: e.dossier_id,
          started_at: e.started_at,
          duration_minutes: e.duration_minutes,
          is_billable: e.is_billable,
        })),
        dossierIds,
      ),
    [entries, dossierIds],
  );

  function exportCsv() {
    const csv = timesheetToCsv(grid, dossierLabels);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `feuille-de-temps-${format(cursor, 'yyyy-MM-dd')}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }

  const collaboratorNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const c of collaborators ?? []) m[c.id] = c.full_name;
    return m;
  }, [collaborators]);

  const modalDossierId = editing?.dossier_id || createDossierId;

  const presentationToggle = (
    <ViewSwitch
      label="Présentation"
      value={presentation}
      onChange={setPresentation}
      options={[
        { value: 'lines', label: 'Registre', icon: <FaList aria-hidden /> },
        { value: 'grid', label: 'Synthèse', icon: <FaTableList aria-hidden /> },
      ]}
    />
  );
  const collaboratorPicker = (
    <PersonSelect
      single
      required
      prefix="Collaborateur"
      value={[collaboratorId ?? myId ?? '']}
      onChange={(ids) => setCollaboratorId(ids[0] && ids[0] !== myId ? ids[0] : undefined)}
    />
  );

  const modal = modalOpen && modalDossierId ? (
    <TimeEntryModal
      open={modalOpen}
      onOpenChange={(open) => {
        setModalOpen(open);
        if (!open) setEditing(null);
      }}
      dossierId={modalDossierId}
      entry={editing}
    />
  ) : null;

  if (presentation === 'lines') {
    return (
      <div className="main-container-gdm">
        <TimesheetLines
          entries={entries ?? []}
          dossierLabels={dossierLabels}
          dossierRefs={dossierRefs}
          collaboratorNames={collaboratorNames}
          onOpen={(entry) => { setEditing(entry); setModalOpen(true); }}
          leading={isAdmin ? collaboratorPicker : null}
          trailing={
            <>
              {presentationToggle}
              <select
                aria-label="Dossier de la nouvelle feuille"
                className="h-9 w-[200px] rounded-full border border-neutral-200 bg-white px-3 text-[13px]"
                value={createDossierId}
                onChange={(e) => setCreateDossierId(e.target.value)}
              >
                <option value="">Dossier…</option>
                {(dossiers ?? []).map((d) => (
                  <option key={d.id} value={d.id}>{dossierLabels[d.id] ?? d.title}</option>
                ))}
              </select>
              <Button
                disabled={!createDossierId}
                onClick={() => { setEditing(null); setModalOpen(true); }}
              >
                <FaPlus className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Nouvelle feuille
              </Button>
            </>
          }
        />
        {modal}
      </div>
    );
  }

  return (
    <ListPageLayout
      scrollable
      header={(
        <div className="flex w-full flex-wrap items-center gap-3">
          {presentationToggle}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              aria-label="Période précédente"
              onClick={() =>
                setCursor((c) => (mode === 'week' ? addWeeks(c, -1) : addMonths(c, -1)))
              }
            >
              ◀
            </Button>
            <span className="text-sm text-neutral-600">
              {mode === 'week'
                ? `Semaine du ${format(startOfWeek(cursor, { weekStartsOn: 1 }), 'dd/MM/yyyy')}`
                : format(cursor, 'MMMM yyyy', { locale: fr })}
            </span>
            <Button
              variant="ghost"
              aria-label="Période suivante"
              onClick={() =>
                setCursor((c) => (mode === 'week' ? addWeeks(c, 1) : addMonths(c, 1)))
              }
            >
              ▶
            </Button>
            <Button
              variant="ghost"
              onClick={() => setMode((m) => (m === 'week' ? 'month' : 'week'))}
            >
              {mode === 'week' ? 'Vue mois' : 'Vue semaine'}
            </Button>
            <Button onClick={exportCsv}>Export CSV</Button>
          </div>

          {isAdmin && collaboratorPicker}
        </div>
      )}
    >
      <TimesheetGrid grid={grid} dossierLabels={dossierLabels} />
      {modal}
    </ListPageLayout>
  );
}
